import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export const DisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-6 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-7 w-7 text-[#C9A64A]" />
              <BarChart3 className="h-4 w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-xl font-bold text-[#1A1A1C] tracking-tight">Virtual<span className="text-[#C9A64A]">CFO</span></span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-[#C9A64A]/10 rounded-full">
              <AlertCircle className="h-5 w-5 text-[#C9A64A]" />
              <span className="text-[#C9A64A] text-sm font-bold tracking-widest uppercase">Legal Notice</span>
            </div>
            <h1 className="text-5xl font-bold text-[#1A1A1C] mb-6">Disclaimer</h1>
            <p className="text-xl text-[#5A6270]">Last updated: January 2025</p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">General Information</h2>
              <p className="text-[#5A6270] leading-relaxed">
                The information provided by Virtual CFO ("we," "us," or "our") on virtualcfo.finance and our related services is for general informational and educational purposes only. All information on the site and our services is provided in good faith; however, we make no representation or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information on the site or our services.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Not Financial Advice</h2>
              <p className="text-[#5A6270] leading-relaxed mb-4">
                The content on virtualcfo.finance, including articles, calculators, and other materials, does not constitute professional financial, investment, tax, or legal advice. You should not act or rely on any information on our website without seeking the advice of a qualified professional advisor.
              </p>
              <p className="text-[#5A6270] leading-relaxed">
                Our Virtual CFO services provide financial planning and analysis support, but should not be considered a substitute for professional accounting, legal, or tax services provided by licensed professionals in your jurisdiction.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">No Guarantees</h2>
              <p className="text-[#5A6270] leading-relaxed">
                While we strive to provide accurate and useful information and tools, we make no guarantees about the results you may achieve by using our services, calculators, or implementing strategies discussed in our content. Business outcomes depend on numerous factors beyond our control, including market conditions, execution, timing, and circumstances specific to your business.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Calculator Limitations</h2>
              <p className="text-[#5A6270] leading-relaxed">
                Our free financial calculators are provided as educational tools to help you understand financial concepts and perform basic calculations. The results should be verified independently and should not be the sole basis for any financial decisions. Calculator outputs are only as accurate as the inputs provided and the assumptions used.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">External Links</h2>
              <p className="text-[#5A6270] leading-relaxed">
                Our website may contain links to external websites that are not provided or maintained by us. We do not guarantee the accuracy, relevance, timeliness, or completeness of any information on these external websites.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Professional Relationship</h2>
              <p className="text-[#5A6270] leading-relaxed">
                Use of our website or free tools does not create a professional relationship between you and Virtual CFO. A formal engagement agreement is required before we provide professional Virtual CFO services.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Limitation of Liability</h2>
              <p className="text-[#5A6270] leading-relaxed">
                Under no circumstance shall we have any liability to you for any loss or damage of any kind incurred as a result of the use of the site or reliance on any information provided on the site. Your use of the site and your reliance on any information on the site is solely at your own risk.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl bg-gradient-to-br from-[#C9A64A]/5 to-transparent border-2 border-[#C9A64A]/20">
              <h2 className="text-2xl font-bold text-[#1A1A1C] mb-4">Contact Us</h2>
              <p className="text-[#5A6270] leading-relaxed mb-4">
                If you have any questions about this Disclaimer, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-[#1A1A1C] font-medium">
                  Email: <a href="mailto:twopearlsprivatelimited@gmail.com" className="text-[#C9A64A] hover:underline">twopearlsprivatelimited@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
